import { Box, Card, CardContent, Chip, makeStyles, Typography, useTheme } from '@material-ui/core'
import { CalendarToday, Schedule } from '@material-ui/icons'
import { Skeleton } from '@material-ui/lab'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SettlementsStatusKPIsResponseModel } from '../../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumTipoCicloFacturacion } from '../../../../api/oData'
import { CardActionProps } from '../../../../components/shared/cardActions/cardActions'
import { CardBottom } from '../../../../components/shared/cardBottom/cardBottom'
import { envConfig } from '../../../../config/envConfig'
import { formatDate } from '../../../../helpers/formatDateHelper'
import { capKpiValue } from '../../../../helpers/numberHelper'
import { CycleExecutionModel } from '../../../invoiceCycles/invoiceCycles.model'
import { SettlementKpiBlock } from '../settlementKpiBlock/settlementKpiBlock'
import { useSettlementKpiBlocksCardStyles } from './settlementKpiBlocksCard.styles'

const useStyles = makeStyles(useSettlementKpiBlocksCardStyles)

interface SettlementKpiBlocksCardProps {
  title?: string
  invoiceCycleType?: EnumTipoCicloFacturacion
  executionData?: CycleExecutionModel | null
  isFetchingExecution?: boolean
  getExecutionState?: (status: number) => void
  footerActions?: CardActionProps[]
  /** Resumen de todas las liquidaciones de la ejecución por estado: si se informa, se muestra el bloque de total como primero de la fila */
  statusKpis?: SettlementsStatusKPIsResponseModel
  isLoadingStatusKpis?: boolean
}

export const SettlementKpiBlocksCard: React.FC<SettlementKpiBlocksCardProps> = ({
  title,
  invoiceCycleType,
  executionData,
  isFetchingExecution = false,
  getExecutionState,
  footerActions,
  statusKpis,
  isLoadingStatusKpis = false,
  children
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements'])
  const theme = useTheme()
  const classes = useStyles(theme)
  const maxDisplayValue = envConfig.kpiConfig.maxDisplayValue

  useEffect(() => {
    if (executionData) {
      getExecutionState?.(executionData.status)
    }
  }, [executionData?.status])

  // Total = pendientes + en curso + facturadas (cerradas y anuladas cuentan como emitidas, igual que en los bloques)
  const totalSettlements = statusKpis
    ? (statusKpis.pending ?? 0) +
      (statusKpis.ongoing ?? 0) +
      (statusKpis.open ?? 0) +
      (statusKpis.closed ?? 0) +
      (statusKpis.cancelled ?? 0) +
      (statusKpis.error ?? 0)
    : undefined

  const getDetailContent = () => {
    if (isFetchingExecution) {
      // Misma estructura que los datos reales para que la card no cambie de alto al cargar
      return (
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="flex-start"
          style={{ columnGap: 40, rowGap: 12 }}
        >
          {[200, 90, 220].map(width => (
            <Box key={width}>
              <Typography variant="caption" className={classes.detailLabel}>
                <Skeleton variant="text" width={60} />
              </Typography>
              <Skeleton variant="rect" width={width} height={24} />
            </Box>
          ))}
        </Box>
      )
    }

    if (!executionData || !invoiceCycleType) {
      return null
    }

    const executionDate = executionData.executionDate
      ? formatDate(executionData.executionDate, 'DD/MM/YYYY - HH:mm:ss')
      : '--/--/---- - --:--:--'

    const cycleType = t(
      `enums:EnumTipoCicloFacturacion.${EnumTipoCicloFacturacion[invoiceCycleType]}`
    )

    const startDate = executionData.startDate
      ? formatDate(executionData.startDate, 'DD/MM/YYYY')
      : '--/--/----'

    const endDate = executionData.endDate
      ? formatDate(executionData.endDate, 'DD/MM/YYYY')
      : '--/--/----'

    return (
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-start"
        style={{ columnGap: 40, rowGap: 12 }}
      >
        <Box>
          <Typography variant="caption" color="textSecondary" className={classes.detailLabel}>
            {t('settlements:components.kpiCard.kpiItems.totalSettlements.executionLabel')}
          </Typography>
          <Box display="flex" alignItems="center">
            <Schedule fontSize="small" style={{ marginRight: 6, color: '#757575' }} />
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {executionDate}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" className={classes.detailLabel}>
            {t('settlements:components.kpiCard.kpiItems.totalSettlements.cycleTypeLabel')}
          </Typography>
          <Box>
            <Chip
              label={cycleType}
              size="small"
              color="primary"
              variant="outlined"
              style={{ fontWeight: 500 }}
            />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" className={classes.detailLabel}>
            {t('settlements:components.kpiCard.kpiItems.totalSettlements.periodLabel')}
          </Typography>
          <Box display="flex" alignItems="center">
            <CalendarToday fontSize="small" style={{ marginRight: 6, color: '#757575' }} />
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {startDate} - {endDate}
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  const hasTotalBlock = !!statusKpis || isLoadingStatusKpis
  const detailContent = getDetailContent()

  return (
    <Card className={classes.root}>
      <CardContent className={classes.container}>
        {(title || detailContent) && (
          <Box className={classes.header}>
            {title && (
              <Typography variant="subtitle2" style={{ fontWeight: 500, fontSize: '1rem' }}>
                {title}
              </Typography>
            )}
            {detailContent}
          </Box>
        )}
        {hasTotalBlock && (
          <Box className={classes.blocks}>
            <SettlementKpiBlock
              isLoading={isLoadingStatusKpis}
              title={t('settlements:components.kpiCard.kpiItems.totalSettlements.totalLabel')}
              value={capKpiValue(totalSettlements, maxDisplayValue)}
              caption={t('settlements:components.kpiCard.kpiItems.totalSettlements.totalCaption')}
            />
          </Box>
        )}
        <Box className={classes.blocks}>{children}</Box>
      </CardContent>
      <CardBottom actions={footerActions} />
    </Card>
  )
}
